import _ from '../../../../packages/@okta/courage-dist/esm/lib/underscore/underscore-min.js';
import { loc } from '../../../util/loc.js';
import fn from '../../../util/FactorUtil.js';
import { AUTHENTICATOR_KEY, ID_PROOFING_TYPE } from '../../ion/RemediationConstants.js';
import { getWebAuthnI18nKey, WEBAUTHN_DISPLAY_NAMES, getWebAuthnI18nParams, WEBAUTHN_I18N_KEYS, isCustomDisplayName } from '../../../util/webauthnDisplayNameUtils.js';

/*!
 * Copyright (c) 2020, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */
const {
  getPasswordComplexityDescriptionForHtmlList: getPasswordComplexityDescriptionForHtmlList
} = fn;

/**
 * Gets the WebAuthn title for enroll or verify views
 * @param {Object} currentViewState - The current view state (remediation object)
 * @param {boolean} isVerify - Whether this is a verify (challenge) flow or enroll flow
 * @returns {string} - The localized title
 */
const getWebAuthnTitle = (currentViewState, isVerify = false) => {
  var _currentViewState$rel, _currentViewState$rel2;
  const displayName = currentViewState === null || currentViewState === void 0 ? void 0 : (_currentViewState$rel = currentViewState.relatesTo) === null || _currentViewState$rel === void 0 ? void 0 : (_currentViewState$rel2 = _currentViewState$rel.value) === null || _currentViewState$rel2 === void 0 ? void 0 : _currentViewState$rel2.displayName;
  const params = getWebAuthnI18nParams(displayName);
  const keyMap = isVerify ? WEBAUTHN_I18N_KEYS.VERIFY_TITLE : WEBAUTHN_I18N_KEYS.ENROLL_TITLE;
  const titleKey = getWebAuthnI18nKey(keyMap, displayName);
  return loc(titleKey, 'login', params);
};

/**
 * Gets the WebAuthn description configuration for select-authenticator
 * @param {Object} authenticator - The authenticator object
 * @param {string} displayName - The displayName from IDX response
 * @param {boolean} isVerifyAuthenticator - Whether this is verify flow
 * @returns {string} - The localized description
 */
const getWebAuthnDescriptionConfig = (authenticator, displayName, isVerifyAuthenticator) => {
  if (isVerifyAuthenticator) {
    return '';
  }
  if (isCustomDisplayName(displayName)) {
    var _authenticator$relate;
    const customDescription = (_authenticator$relate = authenticator.relatesTo) === null || _authenticator$relate === void 0 ? void 0 : _authenticator$relate.description;
    if (customDescription) {
      // Use raw custom description from relatesTo if available
      return customDescription;
    }
    // Fall back to DEFAULT description for custom case without custom description
    const descriptionKey = getWebAuthnI18nKey(WEBAUTHN_I18N_KEYS.DESCRIPTION, WEBAUTHN_DISPLAY_NAMES.DEFAULT);
    return loc(descriptionKey, 'login');
  }

  // For DEFAULT and PASSKEYS cases
  const params = [];
  const descriptionKey = getWebAuthnI18nKey(WEBAUTHN_I18N_KEYS.DESCRIPTION, displayName);
  return loc(descriptionKey, 'login', params);
};

/**
 * Returns the description to display in the additional instructions if present
 * @param {Object} currentViewState - The current view state (remediation object)
 * @returns {string|null} - Returns the description if should display, null otherwise
 */
const getWebAuthnAdditionalInstructions = currentViewState => {
  var _currentViewState$rel3, _currentViewState$rel4;
  const description = currentViewState === null || currentViewState === void 0 ? void 0 : (_currentViewState$rel3 = currentViewState.relatesTo) === null || _currentViewState$rel3 === void 0 ? void 0 : (_currentViewState$rel4 = _currentViewState$rel3.value) === null || _currentViewState$rel4 === void 0 ? void 0 : _currentViewState$rel4.description;
  return description || null;
};
const getButtonDataSeAttr = function (authenticator) {
  if (authenticator.authenticatorKey) {
    var _authenticator$value, _authenticator$value2;
    const method = (_authenticator$value = authenticator.value) !== null && _authenticator$value !== void 0 && _authenticator$value.methodType ? '-' + ((_authenticator$value2 = authenticator.value) === null || _authenticator$value2 === void 0 ? void 0 : _authenticator$value2.methodType) : '';
    return authenticator.authenticatorKey + method;
  }
  return '';
};

/* eslint complexity: [0, 0], max-statements: [2, 32] */
const getAuthenticatorData = function (authenticator, isVerifyAuthenticator) {
  var _authenticator$relate2, _authenticator$relate3, _authenticator$relate4, _authenticator$relate5, _authenticator$relate6, _authenticator$relate7, _authenticator$relate8, _authenticator$relate9, _authenticator$relate10, _authenticator$value3;
  const authenticatorKey = authenticator.authenticatorKey;
  const key = _.isString(authenticatorKey) ? authenticatorKey.toLowerCase() : '';
  let authenticatorData = {};
  let nicknameText = isVerifyAuthenticator ? (_authenticator$relate2 = authenticator.relatesTo) === null || _authenticator$relate2 === void 0 ? void 0 : _authenticator$relate2.nickname : undefined;
  switch (key) {
    case AUTHENTICATOR_KEY.EMAIL:
      Object.assign(authenticatorData, {
        description: isVerifyAuthenticator ? ((_authenticator$relate3 = authenticator.relatesTo) === null || _authenticator$relate3 === void 0 ? void 0 : (_authenticator$relate4 = _authenticator$relate3.profile) === null || _authenticator$relate4 === void 0 ? void 0 : _authenticator$relate4.email) || '' : loc('oie.email.authenticator.description', 'login'),
        iconClassName: 'mfa-okta-email',
        noTranslateClassName: isVerifyAuthenticator ? 'no-translate' : '',
        buttonDataSeAttr: getButtonDataSeAttr(authenticator),
        ariaLabel: isVerifyAuthenticator ? getVerifyEmailAriaLabel((_authenticator$relate5 = authenticator.relatesTo) === null || _authenticator$relate5 === void 0 ? void 0 : (_authenticator$relate6 = _authenticator$relate5.profile) === null || _authenticator$relate6 === void 0 ? void 0 : _authenticator$relate6.email) : loc('oie.select.authenticator.enroll.email.label', 'login')
      });
      break;
    case AUTHENTICATOR_KEY.PASSWORD:
      Object.assign(authenticatorData, {
        description: isVerifyAuthenticator ? '' : loc('oie.password.authenticator.description', 'login'),
        iconClassName: 'mfa-okta-password',
        buttonDataSeAttr: getButtonDataSeAttr(authenticator),
        ariaLabel: isVerifyAuthenticator ? loc('oie.select.authenticator.verify.password.label', 'login') : loc('oie.select.authenticator.enroll.password.label', 'login')
      });
      break;
    case AUTHENTICATOR_KEY.PHONE:
      Object.assign(authenticatorData, {
        nickname: nicknameText,
        description: isVerifyAuthenticator ? (_authenticator$relate7 = authenticator.relatesTo) === null || _authenticator$relate7 === void 0 ? void 0 : (_authenticator$relate8 = _authenticator$relate7.profile) === null || _authenticator$relate8 === void 0 ? void 0 : _authenticator$relate8.phoneNumber : loc('oie.phone.authenticator.description', 'login'),
        iconClassName: 'mfa-okta-phone',
        noTranslateClassName: isVerifyAuthenticator ? 'no-translate' : '',
        buttonDataSeAttr: getButtonDataSeAttr(authenticator),
        ariaLabel: isVerifyAuthenticator ? getVerifyPhoneAriaLabel((_authenticator$relate9 = authenticator.relatesTo) === null || _authenticator$relate9 === void 0 ? void 0 : (_authenticator$relate10 = _authenticator$relate9.profile) === null || _authenticator$relate10 === void 0 ? void 0 : _authenticator$relate10.phoneNumber) : loc('oie.select.authenticator.enroll.phone.label', 'login')
      });
      break;
    case AUTHENTICATOR_KEY.SECURITY_QUESTION:
      Object.assign(authenticatorData, {
        description: isVerifyAuthenticator ? '' : loc('oie.security.question.authenticator.description', 'login'),
        iconClassName: 'mfa-okta-security-question',
        buttonDataSeAttr: getButtonDataSeAttr(authenticator),
        ariaLabel: isVerifyAuthenticator ? loc('oie.select.authenticator.verify.security.question.label', 'login') : loc('oie.select.authenticator.enroll.security.question.label', 'login')
      });
      break;
    case AUTHENTICATOR_KEY.WEBAUTHN:
      {
        var _authenticator$relate11;
        const displayName = (_authenticator$relate11 = authenticator.relatesTo) === null || _authenticator$relate11 === void 0 ? void 0 : _authenticator$relate11.displayName;
        const params = getWebAuthnI18nParams(displayName);
        const description = getWebAuthnDescriptionConfig(authenticator, displayName, isVerifyAuthenticator);

        // Use passkeys icon when displayName is "Passkeys"
        const iconClassName = displayName === WEBAUTHN_DISPLAY_NAMES.PASSKEYS ? 'mfa-passkeys' : 'mfa-webauthn';
        const labelKey = isVerifyAuthenticator ? getWebAuthnI18nKey(WEBAUTHN_I18N_KEYS.SELECT_VERIFY_LABEL, displayName) : getWebAuthnI18nKey(WEBAUTHN_I18N_KEYS.SELECT_ENROLL_LABEL, displayName);
        Object.assign(authenticatorData, {
          description: description,
          iconClassName: iconClassName,
          buttonDataSeAttr: getButtonDataSeAttr(authenticator),
          ariaLabel: loc(labelKey, 'login', params)
        });
        break;
      }
    case AUTHENTICATOR_KEY.OV:
      Object.assign(authenticatorData, {
        description: isVerifyAuthenticator ? loc('oie.okta_verify.label', 'login') : loc('oie.okta_verify.authenticator.description', 'login'),
        iconClassName: 'mfa-okta-verify',
        buttonDataSeAttr: getButtonDataSeAttr(authenticator),
        ariaLabel: getOktaVerifyAriaLabel(isVerifyAuthenticator, authenticator === null || authenticator === void 0 ? void 0 : (_authenticator$value3 = authenticator.value) === null || _authenticator$value3 === void 0 ? void 0 : _authenticator$value3.methodType)
      });
      break;
    case AUTHENTICATOR_KEY.GOOGLE_OTP:
      Object.assign(authenticatorData, {
        description: isVerifyAuthenticator ? '' : loc('oie.google_authenticator.authenticator.description', 'login'),
        iconClassName: 'mfa-google-auth',
        buttonDataSeAttr: getButtonDataSeAttr(authenticator),
        ariaLabel: isVerifyAuthenticator ? loc('oie.select.authenticator.verify.named.authenticator.label', 'login', [authenticator.label]) : loc('oie.select.authenticator.enroll.named.authenticator.label', 'login', [authenticator.label])
      });
      break;
    case AUTHENTICATOR_KEY.ON_PREM:
      {
        var _authenticator$relate12;
        const vendorName = ((_authenticator$relate12 = authenticator.relatesTo) === null || _authenticator$relate12 === void 0 ? void 0 : _authenticator$relate12.displayName) || loc('oie.on_prem.authenticator.default.vendorName', 'login');
        Object.assign(authenticatorData, {
          description: isVerifyAuthenticator ? '' : loc('oie.on_prem.authenticator.description', 'login', [vendorName]),
          iconClassName: 'mfa-onprem',
          buttonDataSeAttr: getButtonDataSeAttr(authenticator),
          ariaLabel: isVerifyAuthenticator ? loc('oie.select.authenticator.verify.named.authenticator.label', 'login', [authenticator.label]) : loc('oie.select.authenticator.enroll.named.authenticator.label', 'login', [authenticator.label])
        });
        break;
      }
    case AUTHENTICATOR_KEY.RSA:
      Object.assign(authenticatorData, {
        description: isVerifyAuthenticator ? '' : loc('oie.rsa.authenticator.description', 'login'),
        iconClassName: 'mfa-rsa',
        buttonDataSeAttr: getButtonDataSeAttr(authenticator),
        ariaLabel: isVerifyAuthenticator ? loc('oie.select.authenticator.verify.named.authenticator.label', 'login', [authenticator.label]) : loc('oie.select.authenticator.enroll.named.authenticator.label', 'login', [authenticator.label])
      });
      break;
    case AUTHENTICATOR_KEY.DUO:
      Object.assign(authenticatorData, {
        description: isVerifyAuthenticator ? '' : loc('oie.duo.authenticator.description', 'login'),
        iconClassName: 'mfa-duo',
        buttonDataSeAttr: getButtonDataSeAttr(authenticator),
        ariaLabel: isVerifyAuthenticator ? loc('oie.select.authenticator.verify.named.authenticator.label', 'login', [authenticator.label]) : loc('oie.select.authenticator.enroll.named.authenticator.label', 'login', [authenticator.label])
      });
      break;
    case AUTHENTICATOR_KEY.IDP:
      {
        var _authenticator$relate13, _authenticator$relate14;
        const idpName = (_authenticator$relate13 = authenticator.relatesTo) === null || _authenticator$relate13 === void 0 ? void 0 : _authenticator$relate13.displayName;
        Object.assign(authenticatorData, {
          description: isVerifyAuthenticator ? '' : loc('oie.idp.authenticator.description', 'login', [idpName]),
          iconClassName: 'mfa-custom-factor',
          buttonDataSeAttr: getButtonDataSeAttr(authenticator),
          logoUri: (authenticator === null || authenticator === void 0 ? void 0 : (_authenticator$relate14 = authenticator.relatesTo) === null || _authenticator$relate14 === void 0 ? void 0 : _authenticator$relate14.logoUri) || '',
          ariaLabel: isVerifyAuthenticator ? loc('oie.select.authenticator.verify.named.authenticator.label', 'login', [authenticator.label]) : loc('oie.select.authenticator.enroll.named.authenticator.label', 'login', [authenticator.label])
        });
        break;
      }
    case AUTHENTICATOR_KEY.CUSTOM_OTP:
      {
        Object.assign(authenticatorData, {
          description: isVerifyAuthenticator ? '' : loc('oie.custom_otp.description', 'login'),
          iconClassName: 'mfa-hotp',
          buttonDataSeAttr: getButtonDataSeAttr(authenticator),
          ariaLabel: isVerifyAuthenticator ? loc('oie.select.authenticator.verify.named.authenticator.label', 'login', [authenticator.label]) : loc('oie.select.authenticator.enroll.named.authenticator.label', 'login', [authenticator.label])
        });
        break;
      }
    case AUTHENTICATOR_KEY.SYMANTEC_VIP:
      {
        var _authenticator$relate15;
        const appName = (_authenticator$relate15 = authenticator.relatesTo) === null || _authenticator$relate15 === void 0 ? void 0 : _authenticator$relate15.displayName;
        Object.assign(authenticatorData, {
          description: isVerifyAuthenticator ? '' : loc('oie.symantecVip.authenticator.description', 'login', [appName]),
          iconClassName: 'mfa-symantec',
          buttonDataSeAttr: getButtonDataSeAttr(authenticator),
          ariaLabel: isVerifyAuthenticator ? loc('oie.select.authenticator.verify.named.authenticator.label', 'login', [authenticator.label]) : loc('oie.select.authenticator.enroll.named.authenticator.label', 'login', [authenticator.label])
        });
        break;
      }
    case AUTHENTICATOR_KEY.YUBIKEY:
      {
        Object.assign(authenticatorData, {
          description: isVerifyAuthenticator ? '' : loc('oie.yubikey.authenticator.description', 'login'),
          iconClassName: 'mfa-yubikey',
          buttonDataSeAttr: getButtonDataSeAttr(authenticator),
          ariaLabel: isVerifyAuthenticator ? loc('oie.select.authenticator.verify.named.authenticator.label', 'login', [authenticator.label]) : loc('oie.select.authenticator.enroll.named.authenticator.label', 'login', [authenticator.label])
        });
        break;
      }
    case AUTHENTICATOR_KEY.CUSTOM_APP:
      {
        var _authenticator$relate16, _authenticator$relate17, _authenticator$relate18;
        Object.assign(authenticatorData, {
          description: isVerifyAuthenticator ? authenticator === null || authenticator === void 0 ? void 0 : (_authenticator$relate16 = authenticator.relatesTo) === null || _authenticator$relate16 === void 0 ? void 0 : _authenticator$relate16.displayName : loc('oie.custom.app.authenticator.description', 'login', [authenticator.label]),
          noTranslateClassName: isVerifyAuthenticator ? 'no-translate' : '',
          buttonDataSeAttr: getButtonDataSeAttr(authenticator),
          iconClassName: 'mfa-custom-app-logo',
          logoUri: (authenticator === null || authenticator === void 0 ? void 0 : (_authenticator$relate17 = authenticator.relatesTo) === null || _authenticator$relate17 === void 0 ? void 0 : _authenticator$relate17.logoUri) || '',
          ariaLabel: isVerifyAuthenticator ? loc('oie.select.authenticator.verify.named.authenticator.label', 'login', [authenticator === null || authenticator === void 0 ? void 0 : (_authenticator$relate18 = authenticator.relatesTo) === null || _authenticator$relate18 === void 0 ? void 0 : _authenticator$relate18.displayName]) : loc('oie.select.authenticator.enroll.named.authenticator.label', 'login', [authenticator.label])
        });
        break;
      }
    case AUTHENTICATOR_KEY.SMARTCARD:
      {
        Object.assign(authenticatorData, {
          description: isVerifyAuthenticator ? '' : loc('oie.smartcard.authenticator.description', 'login'),
          iconClassName: 'mfa-smartcard',
          buttonDataSeAttr: getButtonDataSeAttr(authenticator),
          ariaLabel: isVerifyAuthenticator ? loc('oie.select.authenticator.verify.named.authenticator.label', 'login', [authenticator.label]) : loc('oie.select.authenticator.enroll.named.authenticator.label', 'login', [authenticator.label])
        });
        break;
      }
    case AUTHENTICATOR_KEY.TAC:
      {
        Object.assign(authenticatorData, {
          // we don't expect the description in the else case to be displayed, 
          // because TAC is not eligible for inline enrollment
          description: isVerifyAuthenticator ? '' : loc('oie.tac.authenticator.description', 'login'),
          iconClassName: 'mfa-tac',
          buttonDataSeAttr: getButtonDataSeAttr(authenticator),
          ariaLabel: isVerifyAuthenticator ? loc('oie.select.authenticator.verify.named.authenticator.label', 'login', [authenticator.label]) : loc('oie.select.authenticator.enroll.named.authenticator.label', 'login', [authenticator.label])
        });
        break;
      }
  }
  return authenticatorData;
};
const getIDProofingData = function (idvName) {
  let idProofingData = {};
  switch (idvName) {
    case ID_PROOFING_TYPE.IDV_PERSONA:
      idProofingData = {
        iconClassName: 'mfa-idv-persona'
      };
      break;
    case ID_PROOFING_TYPE.IDV_CLEAR:
      idProofingData = {
        iconClassName: 'mfa-idv-clear'
      };
      break;
    case ID_PROOFING_TYPE.IDV_INCODE:
      idProofingData = {
        iconClassName: 'mfa-idv-incode'
      };
      break;
    case ID_PROOFING_TYPE.IDV_STANDARD:
      idProofingData = {
        iconClassName: 'mfa-idv-standard'
      };
      break;
    case ID_PROOFING_TYPE.IDV_OIN:
      idProofingData = {
        iconClassName: 'mfa-idv-oin'
      };
      break;
  }
  return idProofingData;
};
function getVerifyEmailAriaLabel(email) {
  return email ? loc('oie.select.authenticator.verify.email.with.email.label', 'login', [email]) : loc('oie.select.authenticator.verify.email.label', 'login');
}
function getVerifyPhoneAriaLabel(phone) {
  return phone ? loc('oie.select.authenticator.verify.phone.with.phone.label', 'login', [phone]) : loc('oie.select.authenticator.verify.phone.label', 'login');
}
function getOktaVerifyAriaLabel(isVerify, methodType) {
  if (!isVerify) {
    return loc('oie.select.authenticator.enroll.okta_verify.authenticator.label', 'login');
  }
  const defaultLabel = loc('oie.select.authenticator.verify.okta_verify.label', 'login');
  if (typeof methodType === 'undefined') {
    return defaultLabel;
  }
  const methodTypeLabelMap = {
    push: loc('oie.select.authenticator.okta_verify.push.label', 'login'),
    totp: loc('oie.select.authenticator.okta_verify.totp.label', 'login'),
    'signed_nonce': loc('oie.select.authenticator.okta_verify.signed_nonce.label', 'login')
  };
  return methodTypeLabelMap[methodType] || defaultLabel;
}
function getAuthenticatorDataForEnroll(authenticator) {
  return getAuthenticatorData(authenticator);
}
function getAuthenticatorDataForVerification(authenticator) {
  return getAuthenticatorData(authenticator, true);
}
function getIconClassNameForBeacon(authenticatorKey, idvName, displayName) {
  // Handle WebAuthn with Passkeys displayName
  if (authenticatorKey === AUTHENTICATOR_KEY.WEBAUTHN && displayName === WEBAUTHN_DISPLAY_NAMES.PASSKEYS) {
    return 'mfa-passkeys';
  }
  return getAuthenticatorData({
    authenticatorKey: authenticatorKey
  }).iconClassName || getIDProofingData(idvName).iconClassName;
}
function removeRequirementsFromError(errorJSON) {
  var _errorJSON$errorCause;
  if (((_errorJSON$errorCause = errorJSON.errorCauses) === null || _errorJSON$errorCause === void 0 ? void 0 : _errorJSON$errorCause.length) > 0 && Array.isArray(errorJSON.errorCauses[0].errorSummary) && errorJSON.errorCauses[0].errorSummary.length > 0) {
    var _errorJSON$errorCause2;
    // Change from Array to string for all errors.
    errorJSON.errorCauses[0].errorSummary = errorJSON.errorCauses[0].errorSummary[0];

    // Overrides for particular error messages.
    const errorKey = ((_errorJSON$errorCause2 = errorJSON.errorCauses[0].errorKey) === null || _errorJSON$errorCause2 === void 0 ? void 0 : _errorJSON$errorCause2.length) > 0 && errorJSON.errorCauses[0].errorKey[0];
    // Remove the requirements string only if this is requirements were not met error.
    if (errorKey === 'password.passwordRequirementsNotMet') {
      errorJSON.errorCauses[0].errorSummary = loc('registration.error.password.passwordRequirementsNotMet', 'login');
    }
  }
  return errorJSON;
}

/**
 * Get authenticator display name from {@code remediation}.
 *
 * @param {Object} remediation
 */
function getAuthenticatorDisplayName(remediation) {
  var _remediation$relatesT, _remediation$relatesT2;
  return (_remediation$relatesT = remediation.relatesTo) === null || _remediation$relatesT === void 0 ? void 0 : (_remediation$relatesT2 = _remediation$relatesT.value) === null || _remediation$relatesT2 === void 0 ? void 0 : _remediation$relatesT2.displayName;
}

export { getAuthenticatorDataForEnroll, getAuthenticatorDataForVerification, getAuthenticatorDisplayName, getIconClassNameForBeacon, getOktaVerifyAriaLabel, getPasswordComplexityDescriptionForHtmlList, getVerifyEmailAriaLabel, getVerifyPhoneAriaLabel, getWebAuthnAdditionalInstructions, getWebAuthnTitle, removeRequirementsFromError };
//# sourceMappingURL=AuthenticatorUtil.js.map
